import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Newspaper } from "lucide-react";
import Reveal, { Overline } from "./Reveal";
import { useLightbox } from "../lib/lightbox";

const slug = (s) =>
  String(s || "item").toLowerCase().replace(/[^a-z0-9]+/g, "-");

function ProjectCard({ p, className = "" }) {
  const [current, setCurrent] = useState(0);
  const { open } = useLightbox();
  const gallery = p.gallery || [];
  const total = gallery.length;
  const go = (dir) => setCurrent((current + dir + total) % total);

  return (
    <div
      data-testid={`project-card-${slug(p.id || p.index || p.title)}`}
      className={`group relative overflow-hidden border border-line ${className}`}
    >
      {total > 0 ? (
        <AnimatePresence initial={false}>
          <motion.img
            key={current}
            src={gallery[current]}
            alt={`${p.title} — photo ${current + 1}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 h-full w-full cursor-zoom-in object-cover"
            onClick={() => open(gallery, current)}
            data-testid="project-image-open"
          />
        </AnimatePresence>
      ) : (
        <div className="absolute inset-0 bg-surface" />
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/15 to-transparent" />

      {total > 0 && (
        <div className="absolute right-4 top-4 z-10 flex flex-col items-end gap-2 lg:right-6 lg:top-6">
          <div className="flex items-center gap-2">
            <span className="bg-ink/70 px-2.5 py-2 font-mono text-[10px] tracking-[0.18em] text-slate-200 backdrop-blur-sm">
              {String(current + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
            <button
              data-testid={`project-gallery-prev-${slug(p.id || p.title)}`}
              onClick={() => go(-1)}
              aria-label="Previous photo"
              className="flex h-9 w-9 items-center justify-center border border-white/20 bg-ink/70 text-white backdrop-blur-sm transition-colors duration-300 hover:border-brand hover:bg-brand"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              data-testid={`project-gallery-next-${slug(p.id || p.title)}`}
              onClick={() => go(1)}
              aria-label="Next photo"
              className="flex h-9 w-9 items-center justify-center border border-white/20 bg-ink/70 text-white backdrop-blur-sm transition-colors duration-300 hover:border-brand hover:bg-brand"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="flex gap-1.5">
            {gallery.map((g, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
                className={`h-11 w-16 overflow-hidden border transition-all duration-300 ${
                  i === current
                    ? "border-brand opacity-100"
                    : "border-white/20 opacity-50 hover:opacity-80"
                }`}
              >
                <img src={g} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 p-6 lg:p-8">
        {p.index && (
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand">
            {p.index}
          </div>
        )}
        <h3 className="mt-3 font-display text-2xl uppercase tracking-tight text-white lg:text-3xl">
          {p.title}
        </h3>
        {p.desc && (
          <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-steel">
            {p.desc}
          </p>
        )}
        {p.news_url && (
          <a
            data-testid="project-news-link"
            href={p.news_url}
            target="_blank"
            rel="noopener noreferrer"
            className="pointer-events-auto mt-4 inline-flex items-center gap-2 border border-brand bg-ink/70 px-4 py-2.5 font-mono text-[9px] uppercase tracking-[0.18em] text-white backdrop-blur-sm transition-colors duration-300 hover:bg-brand"
          >
            <Newspaper size={12} />
            Press — {p.news_source || "News coverage"} · Read the story
          </a>
        )}
      </div>
    </div>
  );
}

export default function Projects({ items }) {
  const list = items || [];
  const gridClass = [
    "h-[420px] lg:col-span-7",
    "h-[420px] lg:col-span-5",
    "h-[360px] lg:col-span-12",
  ];

  return (
    <section id="projects" data-testid="projects" className="py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <Overline>Major Operations</Overline>
            <h2 className="mt-6 font-display text-4xl font-medium uppercase leading-[0.95] tracking-tighter sm:text-5xl">
              Proven in the field
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex items-center gap-6 font-mono text-[10px] uppercase tracking-[0.22em] text-steel">
              <span>Selected clients</span>
              <span className="font-display text-sm uppercase tracking-[0.12em] text-slate-100">
                Van Oord
              </span>
              <span className="font-display text-sm uppercase tracking-[0.12em] text-slate-100">
                Petronas
              </span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="mt-12">
          <div className="grid gap-4 lg:grid-cols-12">
            {list.map((p, i) => (
              <ProjectCard
                key={p.id || p.title}
                p={p}
                className={gridClass[i] || "h-[360px] lg:col-span-12"}
              />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
